import {
  checkIfSubscriptionTransaction,
  createTransaction,
  getNewCreditCardIframeUrl,
  retrySubmitCheckout,
} from "./../store/purchasesThunks";
import { useCallback, useState } from "react";
import {
  selectIsSubscriptionPurchase,
  selectIsTransactionExecuting,
  selectJumboCodeQuantity,
  selectNewCreditCardIframeUrl,
  selectNewCreditCardIframeUrlError,
  selectTransactionDetails,
} from "../store/purchasesSelectors";
import { useAppDispatch, useAppSelector } from "./../../../../store/hooks";
import {
  CreateTransactionPayloadT,
  GetNewCreditCardIframeUrlPayloadT,
  PurchaseParamsT,
  RetrySubmitCheckoutPayloadT,
  ValidateTokenPayloadT,
} from "../models/models";
import { validateToken } from "../store/purchasesThunks";
import { AppearanceTypes, useToasts } from "react-toast-notifications";
import { useParams, useSearchParams } from "react-router-dom";
import { useUser } from "../../../../contexts/userContext/userContext";
import { useContent } from "../../../../contexts/contentContext/contentContext";
import usePurchases from "./usePurchases";
import useAddon from "./useAddon";
import useCurrency from "./useCurrency";

const useTransaction = () => {
  const { packageType, periodType } = useParams<PurchaseParamsT>();
  const { isLoggedIn } = useUser();
  const { getTextByKey } = useContent();
  const transactionDetails = useAppSelector(selectTransactionDetails);
  const dispatch = useAppDispatch();
  const newCreditCardIframeUrl = useAppSelector(selectNewCreditCardIframeUrl);
  const jumboCodeQuantity = useAppSelector(selectJumboCodeQuantity);
  const toast = useToasts();
  const { selectedPlanPrice } = usePurchases();
  const { addonData, addonQuantity, existingAddonQuantity } = useAddon();
  const { currency } = useCurrency();
  const newCreditCardIframeUrlError = useAppSelector(
    selectNewCreditCardIframeUrlError
  );
  const isSubscriptionPurchase = useAppSelector(selectIsSubscriptionPurchase);
  const [searchParams] = useSearchParams();

  const getTransactionDetails = useCallback(
    (payload: ValidateTokenPayloadT) => {
      dispatch(validateToken(payload));
    },
    []
  );

  const retryCheckout = useCallback(
    async (payload: RetrySubmitCheckoutPayloadT) => {
      await dispatch(retrySubmitCheckout(payload));
    },
    []
  );

  const submitTransaction = useCallback(
    (isDowngrade: boolean) => {
      const _displayToast = (
        toastTextKey: string,
        appearance: AppearanceTypes
      ) => {
        toast.addToast(getTextByKey(toastTextKey), {
          appearance,
        });
      };

      const addonFirstPaymentPrice = addonData!?.[currency] * addonQuantity;

      const payload: CreateTransactionPayloadT = {
        totalPrice:
          (packageType === "sub-users" || packageType === "extra-storage"
            ? addonFirstPaymentPrice
            : selectedPlanPrice) + "",
        packageType: packageType!,
        periodType: periodType! || jumboCodeQuantity,
        isLoggedIn: isLoggedIn(),
        displayToast: _displayToast,
        addonQuantity: addonQuantity - existingAddonQuantity,
        isDowngrade,
      };
      dispatch(createTransaction(payload));
    },
    [
      isLoggedIn(),
      packageType,
      periodType,
      addonQuantity,
      existingAddonQuantity,
    ]
  );

  const isTransactionExecuting = useAppSelector(selectIsTransactionExecuting);

  /**
   * Fetches the URL for a new credit card iframe by passing
   * transaction encrypted id and transaction token.
   */
  const fetchNewCreditCardIframeUrl = useCallback(
    (payload: GetNewCreditCardIframeUrlPayloadT) => {
      dispatch(getNewCreditCardIframeUrl(payload));
    },
    []
  );

  const checkIfSubscriptionPurchase = useCallback(() => {
    const t = searchParams.get("t");

    if (t) {
      dispatch(checkIfSubscriptionTransaction({ t }));
    }
  }, [searchParams.get("t")]);

  return {
    transactionDetails,
    getTransactionDetails,
    newCreditCardIframeUrl,
    retrySubmitCheckout: retryCheckout,
    createTransaction: submitTransaction,
    isTransactionExecuting,
    fetchNewCreditCardIframeUrl,
    newCreditCardIframeUrlError,
    checkIfSubscriptionPurchase,
    isSubscriptionPurchase,
  };
};

export default useTransaction;
